import { useAtomValue } from 'jotai'
import React from 'react'

import { type ScheduleOption, schedulesAtom } from '../../atoms'
import type { SwitchboardTranslateFn } from '../../types'
import { text } from '../../utils/text'
import { NodeSelect } from './NodeSelect'

export type Schedule = ScheduleOption

export interface ScheduleSelectorProps {
  /** Current selected schedule ID */
  value?: string
  /** Callback when schedule selection changes */
  onChange: (
    scheduleId: string | undefined,
    scheduleUuid: string | undefined,
  ) => void
  /** Placeholder text for the select */
  placeholder?: string
  /** Label for the floating label */
  label?: string
  /** Optional translation function for i18n */
  t?: SwitchboardTranslateFn
  // Note: schedules is provided via Jotai atoms (schedulesAtom)
  // Use useSwitchboardDataAtoms hook in the consuming app to set atoms
}

/**
 * Reusable schedule selector dropdown component.
 *
 * Data Source: Schedules from schedulesAtom (Jotai atom)
 * Fetched via: GET /app/v1/schedules/?pbx_id={id} (Mango Schedule API)
 * Documentation: libraries/switchboard-ui/NODE_DATA_POPULATION/schedule.md
 * Component: ScheduleNode uses ScheduleSelector
 */
export const ScheduleSelector: React.FC<ScheduleSelectorProps> = ({
  value = '',
  onChange,
  placeholder: placeholderProp,
  label: labelProp,
  t,
}) => {
  const placeholder =
    placeholderProp ??
    text(t, 'switchboard.core.selectSchedule', 'Select schedule...')
  const label =
    labelProp ?? text(t, 'switchboard.core.scheduleLabel', 'Schedule')
  const schedules = useAtomValue(schedulesAtom)

  const handleChange = React.useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      const scheduleId = e.target.value || undefined
      const scheduleUuid = schedules.find(
        (schedule) => schedule.id === scheduleId,
      )?.uuid
      onChange(scheduleId, scheduleUuid)
    },
    [onChange, schedules],
  )

  return (
    <NodeSelect label={label} value={value} onChange={handleChange}>
      <option value="">{placeholder}</option>
      {schedules.map((schedule) => (
        <option key={schedule.id} value={schedule.id}>
          {schedule.name || schedule.id}
        </option>
      ))}
    </NodeSelect>
  )
}
